import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Stack,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Grid,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
} from '@mui/material';
import {
    NotificationsActive as NotificationsActiveIcon,
    NotificationsOff as NotificationsOffIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    MarkEmailRead as MarkEmailReadIcon,
    ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import MiniDrawer from '../../../Components/Drawer';
import BaseColor from '../../../Config/Color';
import BaseSetting from '../../../Apis/setting';
import { getApiData } from '../../../Apis/apiHelper';
import styles from './styles';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const getNotificationIcon = (type) => {
    switch (type) {
        case 'success':
            return <CheckCircleIcon color="success" />;
        case 'warning':
            return <WarningIcon color="warning" />;
        case 'error':
            return <ErrorIcon color="error" />;
        case 'inquiry':
            return <InfoIcon color="info" />;
        case 'leave_message':
            return <InfoIcon color="primary" />;
        default:
            return <InfoIcon color="info" />;
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
};

const CompanyUserNotification = () => {

    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const readNotification = async (id) => {
        const url = BaseSetting.endpoint.readNotification;
        const response = await getApiData(BaseSetting?.apiUrl + url, 'patch', { id: id }, {}, true);
        if (response && response.status) {
            getAllNotification();
        }
    }

    const getAllNotification = async () => {
        setLoading(true);
        const url = BaseSetting.endpoint.getAllCustomNotifications;
        const response = await getApiData(BaseSetting?.apiUrl + url, 'GET', {}, {}, true);
        if (response && response.status && Array.isArray(response.data)) {
            // Map API data to grid row structure
            const mapped = response.data.map((item, idx) => ({
                id: item.id,
                type: item.type,
                title: item.title,
                message: item.message,
                sender: {
                    name: item.sender?.name || '-',
                    email: item.sender?.email || '-',
                    phone: item.sender?.phone || '-',
                    role: item.sender?.role || '-',
                },
                recipient: {
                    name: item.recipient?.name || '-',
                    email: item.recipient?.email || '-',
                    phone: item.recipient?.phone || '-',
                    company: item.recipient?.company || '-',
                },
                date: formatDate(item.sent_at || item.createdAt),
                status: item.status || 'unread',
            }));
            setNotifications(mapped);
        } else {
            setNotifications([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        getAllNotification();
    }, []);

    const handleDelete = (id) => {
        setNotifications(notifications.filter(notif => notif.id !== id));
    };

    const handleRefresh = () => {
        getAllNotification();
    };

    const handleOpenDialog = (notification) => {
        setSelectedNotification(notification);
        setOpenDialog(true);
        if (notification.status === 'unread') {
            readNotification(notification.id);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedNotification(null);
    };

    const unreadCount = notifications.filter(notif => notif.status === 'unread').length;

    const columns = [
        {
            field: 'id',
            headerName: 'No.',
            width: 80,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, width: '100%', textAlign: 'center' }}>
                    {params.row.id}
                </Typography>
            ),
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 100,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Tooltip title={params.row.type.replace('_', ' ').toUpperCase()}>
                    <Box display="flex" justifyContent="center" alignItems="center" width="100%">
                        {getNotificationIcon(params.row.type)}
                    </Box>
                </Tooltip>
            ),
        },
        {
            field: 'title',
            headerName: 'Title',
            minWidth: 150,
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Tooltip title={params.row.title}>
                    <Typography
                        onClick={() => handleOpenDialog(params.row)}
                        sx={{
                            cursor: 'pointer',
                            color: 'primary.main',
                            fontWeight: params.row.status === 'unread' ? 600 : 400,
                            fontSize: '0.95rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                            textAlign: 'center',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        {params.row.title}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: 'message',
            headerName: 'Message',
            minWidth: 220,
            flex: 2,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Tooltip title={params.row.message}>
                    <Typography
                        onClick={() => handleOpenDialog(params.row)}
                        sx={{
                            cursor: 'pointer',
                            fontSize: '0.92rem',
                            color: 'text.secondary',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                            textAlign: 'center',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        {params.row.message}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: 'sender',
            headerName: 'Sender',
            minWidth: 140,
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (params) => {
                const s = params.row.sender;
                return `${s.name}${s.role && s.role !== '-' ? ` (${s.role})` : ''}${s.email && s.email !== '-' ? `, Email: ${s.email}` : ''}${s.phone && s.phone !== '-' ? `, Phone: ${s.phone}` : ''}`;
            },
            renderCell: (params) => (
                <Tooltip title={`${params.row.sender.name}${params.row.sender.role && params.row.sender.role !== '-' ? ` (${params.row.sender.role})` : ''}`}>
                    <Box width="100%" textAlign="center">
                        <Typography sx={{ fontSize: '0.93rem', fontWeight: 500 }}>
                            {params.row.sender.name}
                        </Typography>
                        {params.row.sender.role && params.row.sender.role !== '-' && (
                            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                {params.row.sender.role}
                            </Typography>
                        )}
                    </Box>
                </Tooltip>
            ),
        },
        {
            field: 'recipient',
            headerName: 'Recipient',
            minWidth: 140,
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (params) => {
                const r = params.row.recipient;
                return `${r.name}${r.company && r.company !== '-' ? ` (${r.company})` : ''}${r.email && r.email !== '-' ? `, Email: ${r.email}` : ''}${r.phone && r.phone !== '-' ? `, Phone: ${r.phone}` : ''}`;
            },
            renderCell: (params) => (
                <Tooltip title={`${params.row.recipient.name}${params.row.recipient.company && params.row.recipient.company !== '-' ? ` (${params.row.recipient.company})` : ''}`}>
                    <Box width="100%" textAlign="center">
                        <Typography sx={{ fontSize: '0.93rem', fontWeight: 500 }}>
                            {params.row.recipient.name}
                        </Typography>
                        {params.row.recipient.company && params.row.recipient.company !== '-' && (
                            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                {params.row.recipient.company}
                            </Typography>
                        )}
                    </Box>
                </Tooltip>
            ),
        },
        {
            field: 'date',
            headerName: 'Date',
            minWidth: 180,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', width: '100%', textAlign: 'center' }}>
                    {params.row.date}
                </Typography>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            minWidth: 120,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Chip
                    label={params.row.status.charAt(0).toUpperCase() + params.row.status.slice(1)}
                    size="small"
                    sx={{
                        fontWeight: 500,
                        fontSize: '0.85rem',
                        px: 1.5,
                        backgroundColor: params.row.status === 'unread' ? 'primary.light' : 'grey.100',
                        color: params.row.status === 'unread' ? 'primary.main' : 'text.secondary',
                        width: '100%',
                        textAlign: 'center',
                    }}
                />
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            minWidth: 100,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" width="100%">
                    {params.row.status === 'unread' && (
                        <Tooltip title="Mark as read">
                            <IconButton
                                size="small"
                                onClick={() => readNotification(params.row.id)}
                                sx={{ color: 'success.main', p: 0.5 }}
                            >
                                <CheckCircleIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Delete notification">
                        <IconButton
                            size="small"
                            onClick={() => handleDelete(params.row.id)}
                            sx={{ color: 'error.main', p: 0.5 }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
        },
    ];

    const handleDownloadAllExcel = async () => {
        if (!notifications || notifications.length === 0) return;

        try {
            // Map notifications to exportable data
            const data = notifications.map((notif) => ({
                'No.': notif.id,
                'Type': notif.type,
                'Title': notif.title,
                'Message': notif.message,
                'Sender': notif.sender?.name,
                'Sender Email': notif.sender?.email,
                'Sender Phone': notif.sender?.phone,
                'Recipient': notif.recipient?.name,
                'Recipient Email': notif.recipient?.email,
                'Recipient Phone': notif.recipient?.phone,
                'Recipient Company': notif.recipient?.company,
                'Date': notif.date,
                'Status': notif.status,
            }));

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Notifications");

            // Create columns dynamically based on object keys
            worksheet.columns = Object.keys(data[0]).map((key) => ({
                header: key,
                key: key,
                width: 25, // optional: adjust column width
            }));

            // Make header row bold
            worksheet.getRow(1).font = { bold: true };

            // Add all rows
            worksheet.addRows(data);

            // Generate Excel buffer
            const buffer = await workbook.xlsx.writeBuffer();

            // Save Excel file
            saveAs(
                new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                }),
                "all_notifications.xlsx"
            );
        } catch (error) {
            console.error("Error exporting notifications:", error);
        }
    };

    return (
        <MiniDrawer header="Company User Notifications">
            <Box sx={{ width: '100%', mt: 4, px: isMobile ? 2 : 4 }}>
                <Paper
                    elevation={3}
                    sx={styles.headerSection}
                >
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box display="flex" alignItems="center">
                                <NotificationsActiveIcon sx={{ fontSize: 40, mr: 3 }} />
                                <Box>
                                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                                        Notifications
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                        You have {unreadCount} unread notifications
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Stack
                                direction="row"
                                spacing={2}
                                justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                            >

                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    startIcon={<RefreshIcon />}
                                    onClick={handleRefresh}
                                    sx={styles.refreshButton}
                                >
                                    Refresh
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    onClick={handleDownloadAllExcel}
                                    sx={styles.refreshButton}
                                >
                                    Download All as Excel
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Notifications Table */}
                <Paper elevation={2} sx={styles.notificationsTable}>
                    {loading ? (
                        <Box sx={styles.loadingBox}>
                            <CircularProgress size={40} />
                        </Box>
                    ) : notifications.length === 0 ? (
                        <Box sx={styles.emptyNotificationsBox}>
                            <NotificationsOffIcon sx={styles.emptyNotificationsIcon} />
                            <Typography variant="h5" color="text.secondary" sx={styles.emptyNotificationsText}>
                                No notifications yet
                            </Typography>
                            <Typography variant="h6" color="text.secondary">
                                You're all caught up! 🎉
                            </Typography>
                        </Box>
                    ) : (
                        <DataGrid
                            rows={notifications}
                            columns={columns}
                            autoHeight
                            disableRowSelectionOnClick
                            checkboxSelection
                            rowSelectionModel={selectedIds}
                            onRowSelectionModelChange={(newSelection) => {
                                setSelectedIds(newSelection);
                            }}
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                    csvOptions: { allColumns: true },
                                },
                            }}
                            sx={styles.dataGrid}
                        />
                    )}
                </Paper>

                {/* Full Message Dialog */}
                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            p: 0,
                            maxWidth: { xs: '95vw', sm: '700px', md: '800px' },
                            maxHeight: '90vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        }
                    }}
                >
                    {selectedNotification && (
                        <>
                            <DialogTitle sx={{
                                px: { xs: 2, sm: 3, md: 4 },
                                py: 2,
                                borderBottom: '1px solid #eee',
                                background: '#fafbfc',
                                flexShrink: 0,
                                minHeight: 'fit-content'
                            }}>
                                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                    {getNotificationIcon(selectedNotification.type)}
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                            wordBreak: 'break-word',
                                            flex: 1,
                                            minWidth: 0
                                        }}
                                    >
                                        {selectedNotification.title}
                                    </Typography>
                                    <Chip
                                        label={selectedNotification.status.charAt(0).toUpperCase() + selectedNotification.status.slice(1)}
                                        size="small"
                                        color={selectedNotification.status === 'unread' ? 'primary' : 'default'}
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: '0.85rem',
                                            px: 1.5,
                                            flexShrink: 0
                                        }}
                                    />
                                </Box>
                            </DialogTitle>
                            <DialogContent
                                sx={{
                                    px: { xs: 2, sm: 3, md: 4 },
                                    py: 2,
                                    overflow: 'auto',
                                    background: '#fff',
                                    flex: 1,
                                    minHeight: 0,
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#c1c1c1',
                                        borderRadius: '4px',
                                        '&:hover': {
                                            background: '#a8a8a8',
                                        },
                                    },
                                }}
                            >
                                {/* Type, Status, Date Section */}
                                <Box
                                    sx={{
                                        borderBottom: '1px solid #f0f0f0',
                                        pb: 2,
                                        mb: 3,
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: { xs: 1, sm: 2 },
                                        alignItems: 'center'
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        <b>Type:</b> {selectedNotification.type}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        <b>Status:</b> {selectedNotification.status}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        <b>Date:</b> {selectedNotification.date}
                                    </Typography>
                                </Box>

                                {/* Message Section */}
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    sx={{
                                        mb: 1.5,
                                        color: 'primary.main',
                                        fontSize: { xs: '1rem', sm: '1.1rem' }
                                    }}
                                >
                                    Message
                                </Typography>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        background: '#f6f8fa',
                                        borderRadius: 2,
                                        p: { xs: 1.5, sm: 2 },
                                        mb: 3,
                                        maxHeight: '300px',
                                        overflow: 'auto'
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: { xs: '0.95rem', sm: '1.05rem' },
                                            color: 'text.primary',
                                            wordBreak: 'break-word',
                                            lineHeight: 1.6,
                                            whiteSpace: 'pre-wrap'
                                        }}
                                    >
                                        {selectedNotification.message}
                                    </Typography>
                                </Paper>

                                {/* Sender/Recipient Section */}
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={600}
                                            sx={{
                                                mb: 1,
                                                color: 'text.secondary',
                                                fontSize: { xs: '0.9rem', sm: '1rem' }
                                            }}
                                        >
                                            From (Sender)
                                        </Typography>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                borderRadius: 2,
                                                p: { xs: 1.5, sm: 2 },
                                                mb: 2,
                                                minHeight: '120px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Stack spacing={1}>
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight={500}
                                                    sx={{
                                                        color: 'primary.main',
                                                        fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                                        wordBreak: 'break-word',
                                                        lineHeight: 1.3
                                                    }}
                                                >
                                                    {selectedNotification.sender.name}
                                                </Typography>
                                                {selectedNotification.sender.role && selectedNotification.sender.role !== '-' && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                            wordBreak: 'break-word'
                                                        }}
                                                    >
                                                        {selectedNotification.sender.role}
                                                    </Typography>
                                                )}
                                                {selectedNotification.sender.email && selectedNotification.sender.email !== '-' && (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                            wordBreak: 'break-all'
                                                        }}
                                                    >
                                                        <b>Email:</b> {selectedNotification.sender.email}
                                                    </Typography>
                                                )}
                                                {selectedNotification.sender.phone && selectedNotification.sender.phone !== '-' && (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                            wordBreak: 'break-word'
                                                        }}
                                                    >
                                                        <b>Phone:</b> {selectedNotification.sender.phone}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={600}
                                            sx={{
                                                mb: 1,
                                                color: 'text.secondary',
                                                fontSize: { xs: '0.9rem', sm: '1rem' }
                                            }}
                                        >
                                            To (Recipient)
                                        </Typography>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                borderRadius: 2,
                                                p: { xs: 1.5, sm: 2 },
                                                mb: 2,
                                                minHeight: '120px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Stack spacing={1}>
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight={500}
                                                    sx={{
                                                        color: 'primary.main',
                                                        fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                                        wordBreak: 'break-word',
                                                        lineHeight: 1.3
                                                    }}
                                                >
                                                    {selectedNotification.recipient.name}
                                                </Typography>
                                                {selectedNotification.recipient.company && selectedNotification.recipient.company !== '-' && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                            wordBreak: 'break-word'
                                                        }}
                                                    >
                                                        {selectedNotification.recipient.company}
                                                    </Typography>
                                                )}
                                                {selectedNotification.recipient.email && selectedNotification.recipient.email !== '-' && (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                            wordBreak: 'break-all'
                                                        }}
                                                    >
                                                        <b>Email:</b> {selectedNotification.recipient.email}
                                                    </Typography>
                                                )}
                                                {selectedNotification.recipient.phone && selectedNotification.recipient.phone !== '-' && (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                            wordBreak: 'break-word'
                                                        }}
                                                    >
                                                        <b>Phone:</b> {selectedNotification.recipient.phone}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions
                                sx={{
                                    px: { xs: 2, sm: 3, md: 4 },
                                    py: 2,
                                    borderTop: '1px solid #eee',
                                    background: '#fafbfc',
                                    flexShrink: 0
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        width: '100%',
                                        justifyContent: { xs: 'center', sm: 'flex-end' },
                                        flexWrap: 'wrap',
                                        gap: 1
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        startIcon={<DeleteIcon fontSize="small" />}
                                        onClick={() => {
                                            handleDelete(selectedNotification.id);
                                            handleCloseDialog();
                                        }}
                                        sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                            py: { xs: 0.5, sm: 0.75 },
                                            px: { xs: 1.5, sm: 2 }
                                        }}
                                    >
                                        Delete
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleCloseDialog}
                                        size="small"
                                        sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                            py: { xs: 0.5, sm: 0.75 },
                                            px: { xs: 1.5, sm: 2 }
                                        }}
                                    >
                                        Close
                                    </Button>
                                </Stack>
                            </DialogActions>
                        </>
                    )}
                </Dialog>
            </Box>
        </MiniDrawer>
    );
};

export default CompanyUserNotification; 